import { z } from 'zod';

/**
 * Frontend feature flags for Security and Legal & Compliance.
 *
 * Frontend-only: flags hide navigation and block routes. They do NOT disable
 * backend services or APIs. Two levels: a section flag and per-module flags.
 * Default-ON: anything not explicitly `false` is enabled. A missing or malformed
 * VITE_FEATURE_FLAGS value enables everything.
 */

export type SectionId = 'security' | 'legal';

interface SectionDef {
  /** Shared URL prefix for the section, or null if the section has no common prefix. */
  sectionPrefix: string | null;
  /** module key -> route prefixes that module owns. */
  modules: Record<string, string[]>;
}

/** Single source of truth mapping sections/modules to the routes they own. */
export const FEATURE_REGISTRY: Record<SectionId, SectionDef> = {
  security: {
    // VIS-197: all security module routes now live under /security.
    sectionPrefix: '/security',
    modules: {
      incidents: ['/security/incidents'],
      executiveProtection: ['/security/executive-protection'],
      threats: ['/security/threats'],
      investigations: ['/security/investigations'],
      subjects: ['/security/subjects'],
    },
  },
  legal: {
    sectionPrefix: '/legal',
    modules: {
      vendors: ['/legal/vendors'],
      matters: ['/legal/matters'],
      governance: ['/legal/governance'],
      compliance: ['/legal/compliance'],
      policies: ['/legal/policies'],
      tracking: ['/legal/tracking'],
      hipaa: ['/legal/hipaa'],
      referenceLists: ['/legal/admin/reference-lists'],
      customContracts: ['/legal/custom-contracts'],
    },
  },
};

export interface SectionFlags {
  enabled?: boolean;
  modules?: Record<string, boolean>;
}

export type FeatureFlagState = Partial<Record<SectionId, SectionFlags>>;

const sectionSchema = z.object({
  enabled: z.boolean().optional(),
  modules: z.record(z.string(), z.boolean()).optional(),
});

// .partial() makes both `security` and `legal` optional, so the section schema
// itself does not need to be optional.
const featureFlagsSchema = z
  .object({
    security: sectionSchema,
    legal: sectionSchema,
  })
  .partial();

/**
 * Parse and validate the raw VITE_FEATURE_FLAGS string. Returns an empty state
 * (all features enabled) when the value is absent, empty, malformed, or invalid.
 */
export function parseFeatureFlags(raw: string | undefined): FeatureFlagState {
  if (!raw || raw.trim() === '') {
    return {};
  }

  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (error) {
    console.warn(
      '[features] VITE_FEATURE_FLAGS is not valid JSON; enabling all features.',
      error
    );
    return {};
  }

  const result = featureFlagsSchema.safeParse(json);
  if (!result.success) {
    console.warn(
      '[features] VITE_FEATURE_FLAGS failed validation; enabling all features.',
      result.error.issues
    );
    return {};
  }

  // Drop keys the schema left as undefined so the object compares cleanly.
  const cleaned: FeatureFlagState = {};
  if (result.data.security) {
    cleaned.security = result.data.security;
  }
  if (result.data.legal) {
    cleaned.legal = result.data.legal;
  }
  return cleaned;
}

/** Pure resolver: is a section (or a module within it) enabled given a state? */
export function resolveIsEnabled(
  state: FeatureFlagState,
  section: SectionId,
  module?: string
): boolean {
  const sectionFlags = state[section];
  if (sectionFlags?.enabled === false) {
    return false;
  }
  if (module) {
    return sectionFlags?.modules?.[module] !== false;
  }
  return true;
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Pure resolver: is the feature owning `pathname` enabled?
 * Module-owned paths use the module flag; paths under a section prefix but owned
 * by no module are gated by the section flag only; all other paths are enabled.
 */
export function resolvePathEnabled(state: FeatureFlagState, pathname: string): boolean {
  const sectionIds = Object.keys(FEATURE_REGISTRY) as SectionId[];

  // 1. Most specific: a module that owns this path.
  for (const section of sectionIds) {
    const { modules } = FEATURE_REGISTRY[section];
    for (const [moduleKey, prefixes] of Object.entries(modules)) {
      if (prefixes.some((prefix) => matchesPrefix(pathname, prefix))) {
        return resolveIsEnabled(state, section, moduleKey);
      }
    }
  }

  // 2. Section-owned (orphan) path: gated by the section flag only.
  for (const section of sectionIds) {
    const { sectionPrefix } = FEATURE_REGISTRY[section];
    if (sectionPrefix && matchesPrefix(pathname, sectionPrefix)) {
      return resolveIsEnabled(state, section);
    }
  }

  // 3. Not part of any flagged feature.
  return true;
}

/**
 * Resolve the raw feature-flags string for the running app.
 *
 * Resolution order:
 *  1. Runtime config (`window.__APP_CONFIG__.featureFlags`) injected by the
 *     container entrypoint from the `FEATURE_FLAGS` env var. This lets a single
 *     shared web-client image be configured per-deployment (e.g. one site shows
 *     only Legal & Compliance) without rebuilding.
 *  2. Build-time `VITE_FEATURE_FLAGS` (`import.meta.env`) — used for local dev,
 *     where there is no entrypoint to inject runtime config.
 */
export function resolveRawFlags(): string | undefined {
  const runtime =
    typeof window !== 'undefined' ? window.__APP_CONFIG__?.featureFlags : undefined;
  if (typeof runtime === 'string' && runtime.trim() !== '') {
    return runtime;
  }
  return import.meta.env.VITE_FEATURE_FLAGS;
}

/** Resolved flag state for the running app, parsed once at module load. */
const featureFlagState: FeatureFlagState = parseFeatureFlags(resolveRawFlags());

/** App-facing: is a section (or module) enabled in the current build? */
export function isFeatureEnabled(section: SectionId, module?: string): boolean {
  return resolveIsEnabled(featureFlagState, section, module);
}

/** App-facing: is the feature owning `pathname` enabled in the current build? */
export function isPathEnabled(pathname: string): boolean {
  return resolvePathEnabled(featureFlagState, pathname);
}
